const MENU_FETCH_TIMEOUT_MS = 10000;

async function fetchMenuJson(url, options, errorLabel) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), MENU_FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    if (!response.ok) {
      throw new Error(`${errorLabel}: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error(`${errorLabel}: timed out`);
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

class CustomMenu extends HTMLElement {
  constructor() {
    super();

    this.assetUrl = this.dataset.menuAsset;
    this.assetDataPromise = null;
    this.assetWarningLogged = false;
    this.expectedMarket = this.dataset.menuMarket || '';
    this.rootHandle = this.dataset.rootHandle || '__root';
    this.labels = {
      all: this.dataset.allLabel || 'All',
      empty: this.dataset.emptyLabel || 'No menu items found.',
      loadError: this.dataset.loadErrorLabel || 'Could not load the menu.',
      loading: this.dataset.loadingLabel || 'Loading menu...',
      menu: this.dataset.menuLabel || 'Menu',
      viewAllTemplate: this.dataset.viewAllTemplate || 'View all [title]',
      subcategoriesTemplate: this.dataset.subcategoriesTemplate || '[title]',
    };
    this.cache = new Map();
    this.stack = [];
    this.activeHandle = null;

    this.drawer = this.querySelector('.custom-menu__drawer');
    this.openButton = this.querySelector('[data-menu-open]');
    this.triggers = [this.openButton, ...document.querySelectorAll('[data-menu-open-proxy]')].filter(Boolean);
    this.closeButtons = this.querySelectorAll('[data-menu-close]');
    this.closeButton = this.querySelector('.custom-menu__close');
    this.backButton = this.querySelector('[data-menu-back]');
    this.status = this.querySelector('[data-menu-status]');
    this.list = this.querySelector('[data-menu-list]');
    this.panelTitle = this.querySelector('[data-menu-panel-title]');
    this.panelLink = this.querySelector('[data-menu-panel-link]');
    this.panelGrid = this.querySelector('[data-menu-panel-grid]');
    this.rootFallbackData = null;

    this.hydrateRootFromMarkup();
    this.bindEvents();
  }

  bindEvents() {
    this.triggers.forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
      if (this.drawer?.id) button.setAttribute('aria-controls', this.drawer.id);
      button.addEventListener('click', () => this.open(this.rootHandle, this.labels.menu, { opener: button }));
    });
    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
    this.backButton?.addEventListener('click', () => this.goBack());

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
    });

    // Wrap Tab at the drawer's ends here as well. main.js trapFocus() only arms
    // its Tab handler on a focusin, and re-trapping after a render focuses an
    // element that already has focus - no focusin - so Shift+Tab from the close
    // button walked out to the shortcut bar behind the open drawer.
    this.drawer?.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !this.classList.contains('is-open')) return;
      const focusable = Array.from(this.drawer.querySelectorAll('a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), [tabindex]:not([tabindex^="-"])'))
        .filter((el) => el.offsetParent && getComputedStyle(el).visibility !== 'hidden');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  hydrateRootFromMarkup() {
    if (!this.list) return;

    const children = Array.from(this.list.children)
      .filter((element) => element.matches('[data-menu-item]'))
      .map((element) => this.itemFromElement(element))
      .filter((item) => item.title);

    if (!children.length) return;

    this.rootFallbackData = { children };
    if (!this.assetUrl) {
      this.cache.set(this.rootHandle, this.rootFallbackData);
    }
    this.bindListDrilldowns();
  }

  itemFromElement(element) {
    const link = element.querySelector('[data-menu-link]');
    const title = element.dataset.menuTitle || link?.textContent?.trim() || '';

    return {
      title,
      url: element.dataset.menuUrl || link?.getAttribute('href') || '#',
      handle: element.dataset.menuHandle || '',
      hasChildren: element.dataset.menuHasChildren === 'true',
    };
  }

  bindListDrilldowns() {
    this.list?.querySelectorAll('[data-menu-link]').forEach((link) => {
      if (link.dataset.menuLinkBound === 'true') return;

      link.dataset.menuLinkBound = 'true';
      link.addEventListener('click', (event) => {
        const itemElement = link.closest('[data-menu-item]');
        if (!itemElement) return;

        this.handleItemLinkClick(event, this.itemFromElement(itemElement));
      });
    });

    this.list?.querySelectorAll('[data-menu-drilldown]').forEach((button) => {
      if (button.dataset.menuBound === 'true') return;

      button.dataset.menuBound = 'true';
      button.addEventListener('click', () => {
        const itemElement = button.closest('[data-menu-item]');
        if (!itemElement) return;

        this.drill(this.itemFromElement(itemElement));
      });
    });
  }

  async open(handle = this.rootHandle, label = this.labels.menu, options = {}) {
    this.classList.add('is-open');
    this.drawer?.setAttribute('aria-hidden', 'false');
    document.body.classList.add('custom-menu-lock');
    this.opener = options.opener || null;
    this.triggers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
    this.trap(this.closeButton);

    await this.renderLevel(handle, label, false);

    if (options.selectHandle) {
      await this.selectItem({
        handle: options.selectHandle,
        title: options.selectTitle || options.selectHandle,
        url: options.selectUrl || `/collections/${options.selectHandle}`,
        hasChildren: options.selectHasChildren !== false,
      }, false);
    }
    this.trap();
  }

  // trapFocus() (main.js) snapshots the drawer's focusable elements, so it is
  // re-run after every render that replaces the list or the panel.
  trap(elementToFocus, attempts = 0) {
    if (!this.classList.contains('is-open')) return;
    // The drawer fades in with a visibility transition, and focus() on a still
    // hidden element is silently ignored - so opening with a fast (cached) menu
    // left focus on the trigger. Wait the few frames until it is visible.
    if (getComputedStyle(this.drawer).visibility !== 'visible' && attempts < 30) {
      requestAnimationFrame(() => this.trap(elementToFocus, attempts + 1));
      return;
    }
    const current = this.drawer.contains(document.activeElement) ? document.activeElement : null;
    trapFocus(this.drawer, elementToFocus || current || this.closeButton);
  }

  close() {
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    this.drawer?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('custom-menu-lock');
    this.stack = [];
    this.activeHandle = null;
    this.backButton.hidden = true;
    this.triggers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
    removeTrapFocus(this.opener);
  }

  async goBack() {
    const previous = this.stack.pop();
    if (!previous) return;
    const from = this.activeHandle;
    await this.renderLevel(previous.handle, previous.label, false);
    const item = [...this.list.querySelectorAll('[data-menu-item]')].find((el) => el.dataset.menuHandle === from);
    this.trap(item?.querySelector('[data-menu-drilldown]') || item?.querySelector('a') || this.firstListControl());
  }

  // User-initiated drill-down: desktop shows the panel, mobile replaces the list.
  async drill(item) {
    const replacesList = await this.selectItem(item, true);
    this.trap(replacesList ? this.firstListControl() : this.panelTitle);
  }

  firstListControl() {
    return this.list.querySelector('a, button');
  }

  isPanelVisible() {
    return !window.matchMedia('(max-width: 768px)').matches;
  }

  async renderLevel(handle, label, pushCurrent = true) {
    if (pushCurrent && this.activeHandle) {
      this.stack.push({ handle: this.activeHandle, label: this.panelTitle?.textContent || this.labels.menu });
    }

    this.activeHandle = handle;
    this.backButton.hidden = this.stack.length === 0;
    this.setStatus(this.labels.loading);

    let data;
    try {
      data = await this.loadLevel(handle);
    } catch (error) {
      if (handle === this.rootHandle && this.rootFallbackData) {
        data = this.rootFallbackData;
      } else {
        this.renderList([]);
        this.renderPanel({ title: label, url: '#', handle, hasChildren: false }, []);
        this.setStatus(this.labels.loadError);
        return;
      }
    }

    if (!data) {
      this.renderList([]);
      this.renderPanel({ title: label, url: '#', handle, hasChildren: false }, []);
      this.setStatus(this.labels.loadError);
      return;
    }

    const children = Array.isArray(data?.children) ? data.children : [];

    this.renderList(children);
    this.setStatus(children.length ? '' : this.labels.empty);

    const firstWithChildren = children.find((child) => child.hasChildren) || children[0];
    if (firstWithChildren) {
      await this.selectItem(firstWithChildren, false);
    } else {
      this.renderPanel({ title: label, url: '#', handle, hasChildren: false }, []);
    }
  }

  async loadLevel(handle) {
    if (this.cache.has(handle)) return this.cache.get(handle);

    if (!this.assetUrl) throw new Error('Missing menu asset');

    try {
      const data = await this.loadAssetData();
      const normalizedData = this.levelFromAsset(data, handle);
      this.cache.set(handle, normalizedData);
      return normalizedData;
    } catch (error) {
      this.warnAssetFailure(error);
      throw error;
    }
  }

  async loadAssetData() {
    if (this.assetDataPromise) return this.assetDataPromise;

    this.assetDataPromise = fetchMenuJson(this.assetUrl, {
      cache: 'force-cache',
      headers: { accept: 'application/json' },
    }, 'Menu asset request failed').then((data) => this.validateAssetData(data)).catch((error) => {
      this.assetDataPromise = null;
      throw error;
    });

    return this.assetDataPromise;
  }

  validateAssetData(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      throw new Error('Menu asset was not an object');
    }
    if (data.version !== 1) {
      throw new Error(`Unsupported menu asset version: ${String(data.version)}`);
    }
    if (data.placeholder) {
      throw new Error('Menu asset is placeholder');
    }
    if (this.expectedMarket && data?.market?.key !== this.expectedMarket) {
      throw new Error(`Menu asset market mismatch: expected ${this.expectedMarket}`);
    }
    if (data.rootHandle !== this.rootHandle) {
      throw new Error(`Menu asset root mismatch: expected ${this.rootHandle}`);
    }
    if (!data.levels || typeof data.levels !== 'object' || Array.isArray(data.levels)) {
      throw new Error('Menu asset did not include a valid levels object');
    }

    return data;
  }

  levelFromAsset(data, handle) {
    const levels = data.levels;
    if (!Object.prototype.hasOwnProperty.call(levels, handle)) {
      throw new Error(`Menu asset did not include handle: ${handle}`);
    }

    const level = levels[handle];
    if (!level
      || typeof level !== 'object'
      || Array.isArray(level)
      || level.handle !== handle
      || !Array.isArray(level.children)) {
      throw new Error(`Menu asset included an invalid level: ${handle}`);
    }
    level.children.forEach((child) => this.validateAssetChild(child, levels));

    return this.normalizeLevelData(level, handle);
  }

  validateAssetChild(child, levels) {
    if (!child || typeof child !== 'object' || Array.isArray(child)) {
      throw new Error('Menu asset included an invalid child');
    }
    if (!child.handle || typeof child.handle !== 'string') {
      throw new Error('Menu asset child did not include a handle');
    }
    if (typeof child.title !== 'string' || typeof child.url !== 'string' || typeof child.type !== 'string') {
      throw new Error(`Menu asset child included invalid text fields: ${child.handle}`);
    }
    if (child.image !== null && child.image !== undefined && typeof child.image !== 'string') {
      throw new Error(`Menu asset child included an invalid image: ${child.handle}`);
    }
    if (typeof child.hasChildren !== 'boolean'
      || !Number.isInteger(child.childCount)
      || child.childCount < 0
      || child.hasChildren !== (child.childCount > 0)) {
      throw new Error(`Menu asset child included invalid child metadata: ${child.handle}`);
    }
    if (child.hasChildren) {
      const childLevel = levels[child.handle];
      if (!Object.prototype.hasOwnProperty.call(levels, child.handle)
        || !childLevel
        || typeof childLevel !== 'object'
        || Array.isArray(childLevel)
        || !Array.isArray(childLevel.children)
        || childLevel.children.length !== child.childCount) {
        throw new Error(`Menu asset omitted child level: ${child.handle}`);
      }
    }
  }

  normalizeLevelData(data, fallbackHandle = '') {
    const children = Array.isArray(data?.children) ? data.children : [];

    return {
      ...data,
      handle: data?.handle || fallbackHandle,
      children: children.map((child) => this.normalizeChild(child)),
    };
  }

  normalizeChild(child) {
    return {
      handle: child?.handle || '',
      title: child?.title || '',
      url: child?.url || '#',
      type: child?.type || 'collection',
      image: child?.image || null,
      hasChildren: Boolean(child?.hasChildren),
      childCount: Number.isFinite(Number(child?.childCount)) ? Number(child.childCount) : 0,
    };
  }

  warnAssetFailure(error) {
    if (this.assetWarningLogged) return;
    this.assetWarningLogged = true;
    if (window?.console?.warn) {
      console.warn('Generated menu asset could not be loaded; using fallback endpoint if available.', error);
    }
  }

  renderList(children) {
    this.list.innerHTML = '';

    children.forEach((item) => {
      const li = document.createElement('li');
      li.className = `custom-menu__item${item.hasChildren ? ' custom-menu__item--has-children' : ''}`;
      li.dataset.menuItem = '';
      li.dataset.menuTitle = item.title || '';
      li.dataset.menuUrl = item.url || '#';
      li.dataset.menuHandle = item.handle || '';
      li.dataset.menuHasChildren = item.hasChildren ? 'true' : 'false';

      const link = document.createElement('a');
      link.className = 'custom-menu__item-link';
      link.href = item.url || '#';
      link.dataset.menuLink = '';
      link.addEventListener('click', (event) => this.handleItemLinkClick(event, item));
      link.innerHTML = `<span data-menu-title>${this.escapeHtml(item.title)}</span>`;
      li.appendChild(link);

      if (item.hasChildren) {
        const button = document.createElement('button');
        button.className = 'custom-menu__item-button';
        button.type = 'button';
        button.dataset.menuDrilldown = '';
        button.dataset.menuHandle = item.handle || '';
        button.setAttribute('aria-label', this.labels.subcategoriesTemplate.replace('[title]', item.title || ''));
        button.addEventListener('click', () => this.drill(item));
        button.innerHTML = '<span class="custom-menu__item-arrow" aria-hidden="true">&rsaquo;</span>';
        li.appendChild(button);
      }

      this.list.appendChild(li);
    });
  }

  handleItemLinkClick(event, item) {
    if (!item.hasChildren || this.shouldLetLinkNavigate(event)) return;
    if (event.detail === 0) return;

    const target = event.target instanceof Element ? event.target : null;
    const title = target?.closest('[data-menu-title]');
    if (title && event.currentTarget.contains(title)) return;

    event.preventDefault();
    this.drill(item);
  }

  async selectItem(item, allowMobileDrilldown) {
    const panelVisible = this.isPanelVisible();
    this.list.querySelectorAll('[data-menu-item]').forEach((element) => {
      const active = element.dataset.menuHandle === item.handle;
      element.classList.toggle('is-active', active);
      const button = element.querySelector('[data-menu-drilldown]');
      if (!button) return;
      // The button only discloses the side panel where the panel is shown.
      if (panelVisible) {
        button.setAttribute('aria-expanded', active ? 'true' : 'false');
        button.setAttribute('aria-controls', 'custom-menu-panel');
      } else {
        button.removeAttribute('aria-expanded');
        button.removeAttribute('aria-controls');
      }
    });

    if (allowMobileDrilldown && !panelVisible && item.hasChildren) {
      await this.renderLevel(item.handle, item.title, true);
      return true;
    }

    let data;
    try {
      data = item.hasChildren ? await this.loadLevel(item.handle) : { children: [] };
    } catch (error) {
      this.setStatus(this.labels.loadError);
      this.renderPanel(item, []);
      return;
    }

    const children = Array.isArray(data?.children) ? data.children : [];
    this.renderPanel(item, children);
  }

  renderPanel(itemOrTitle, children = []) {
    const item = typeof itemOrTitle === 'string'
      ? { title: itemOrTitle, url: '#', handle: this.activeHandle, hasChildren: false }
      : itemOrTitle;

    if (this.panelTitle) this.panelTitle.textContent = item.title || this.labels.menu;
    if (this.panelLink) {
      this.panelLink.hidden = true;
    }

    this.panelGrid.innerHTML = '';
    if (item.url) {
      const viewAll = document.createElement('a');
      viewAll.className = 'custom-menu__tile custom-menu__tile--view-all';
      viewAll.href = item.url;
      viewAll.innerHTML = `
        <span class="custom-menu__tile-media custom-menu__tile-media--view-all">${this.mediaFor(item, this.labels.all)}</span>
        <span>${this.escapeHtml(this.viewAllLabel(item.title || ''))}</span>
      `;
      this.panelGrid.appendChild(viewAll);
    }

    children.forEach((child) => {
      const tile = document.createElement('a');
      tile.className = 'custom-menu__tile';
      tile.href = child.url || '#';

      if (child.hasChildren) {
        tile.addEventListener('click', (event) => {
          if (this.shouldLetLinkNavigate(event)) return;

          event.preventDefault();
          this.renderLevel(child.handle, child.title, true).then(() => this.trap(this.firstListControl()));
        });
      }

      tile.innerHTML = `
        <span class="custom-menu__tile-media">${this.mediaFor(child)}</span>
        <span>${this.escapeHtml(child.title)}</span>
      `;
      this.panelGrid.appendChild(tile);
    });
  }

  shouldLetLinkNavigate(event) {
    return event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey;
  }

  mediaFor(item, fallbackText = null) {
    if (item.image) {
      return `<img src="${this.escapeAttribute(item.image)}" alt="">`;
    }

    return `<span aria-hidden="true">${this.escapeHtml(fallbackText || this.logoText(item.title))}</span>`;
  }

  viewAllLabel(_title) {
    return this.labels.viewAllTemplate.replace('[title]', '').replace(/\s+/g, ' ').trim() || this.labels.all;
  }

  iconFor(item) {
    const title = (item.title || '').toLowerCase();
    if (title.includes('mobil')) return '[]';
    if (title.includes('ladd')) return '~';
    if (title.includes('ljud')) return '()';
    if (title.includes('power')) return '^';
    if (title.includes('batter')) return '+';
    if (title.includes('kampanj')) return '%';
    return '*';
  }

  logoText(title) {
    const words = String(title || '')
      .split(/\s+/)
      .filter(Boolean);

    if (!words.length) return '*';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return words.slice(0, 2).map((word) => word[0]).join('').toUpperCase();
  }

  setStatus(message) {
    if (!this.status) return;
    this.status.hidden = !message;
    this.status.textContent = message;
  }

  escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    }[char]));
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replace(/`/g, '&#096;');
  }
}

customElements.define('custom-menu', CustomMenu);
